import {
  createBrowserHistory,
  createMemoryHistory,
  History,
  MemoryHistoryBuildOptions,
  BrowserHistoryBuildOptions,
} from 'history'

const isServer = !(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
)

export type CreateHistoryOptions =
  | MemoryHistoryBuildOptions
  | BrowserHistoryBuildOptions

let clientSingleton: History | undefined

// Returns a browser-history singleton on the client (cached so thunks
// importing this module reach the same history the app tree is bound
// to) and a fresh memory history per call on the server. mini_racer
// reuses its V8 context across SSR requests, so the server branch
// deliberately does NOT cache — caching would leak the previous
// request's URL into the next render. Tests pass forceMemoryHistory
// to swap the cached client singleton with a per-test memory history;
// without that, ReduxRouterProvider and thunk-imported getClientHistory()
// would diverge and URL updates from thunks would never reach the
// React Router context.
const createAppHistory = (
  options: CreateHistoryOptions = {},
  forceMemoryHistory = false,
): History => {
  if (isServer) {
    return createMemoryHistory(options as MemoryHistoryBuildOptions)
  }
  if (forceMemoryHistory) {
    clientSingleton = createMemoryHistory(options as MemoryHistoryBuildOptions)
    return clientSingleton
  }
  if (!clientSingleton) {
    clientSingleton = createBrowserHistory(
      options as BrowserHistoryBuildOptions,
    )
  }
  return clientSingleton
}

// Thunk-friendly accessor: returns the cached client singleton,
// creating it lazily on first call. Intended for non-React code
// (action creators, side-effect modules) that needs imperative
// navigation. Calling this server-side throws — thunks should never
// run during SSR.
export const getClientHistory = (): History => {
  if (isServer) {
    throw new Error(
      'getClientHistory() called server-side. Thunks must not run during SSR.',
    )
  }
  if (!clientSingleton) {
    clientSingleton = createBrowserHistory()
  }
  return clientSingleton
}

export default createAppHistory
